import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Plus, Trash2, Phone, Volume2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { KioskViewport, speakText, useKioskSpeech } from "../../components/ui/kiosk-viewport";
import { KioskHeader } from "../../components/ui/kiosk-header";

const IDLE_TIMEOUT = 60;

// Virtual Keyboard Component
function VirtualKeyboard({ onKeyPress, onDelete, onClose, mode = 'text' }) {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const keyboardRef = useRef(null);

  const textKeys = [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['Z', 'X', 'C', 'V', 'B', 'N', 'M', 'DEL']
  ];

  const emailKeys = [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['Z', 'X', 'C', 'V', 'B', 'N', 'M', 'DEL'],
    ['@', '.', '-', '_', '.com', '.co.uk']
  ];

  const phoneKeys = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['+', '0', 'DEL']
  ];

  const keys = mode === 'phone' ? phoneKeys : mode === 'email' ? emailKeys : textKeys;

  const handleKey = (key) => {
    if (key === 'DEL') {
      onDelete();
    } else {
      onKeyPress(key);
    }
  };

  const handleDragStart = (e) => {
    if (e.target.closest('[data-drag-handle]')) {
      setIsDragging(true);
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      dragStartRef.current = { x: clientX - position.x, y: clientY - position.y };
      e.preventDefault();
    }
  };

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const newX = clientX - dragStartRef.current.x;
    const newY = clientY - dragStartRef.current.y;
    const keyboard = keyboardRef.current;
    if (keyboard) {
      const rect = keyboard.getBoundingClientRect();
      const maxX = window.innerWidth - rect.width;
      const maxY = window.innerHeight - rect.height;
      setPosition({
        x: Math.max(-maxX/2, Math.min(maxX/2, newX)),
        y: Math.max(-maxY, Math.min(100, newY))
      });
    } else {
      setPosition({ x: newX, y: newY });
    }
  }, [isDragging]);

  const handleDragEnd = useCallback(() => setIsDragging(false), []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: false });
      window.addEventListener('touchend', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDragMove);
        window.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  return (
    <div 
      ref={keyboardRef}
      style={{ 
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: `translateX(calc(-50% + ${position.x}px)) translateY(${position.y}px)`,
        touchAction: 'none',
        maxWidth: mode === 'phone' ? '400px' : '95%',
        width: mode === 'phone' ? '400px' : '1000px',
        background: '#1e293b',
        padding: '16px',
        borderRadius: '16px 16px 0 0',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.3)',
        zIndex: 50
      }}
      onMouseDown={handleDragStart}
      onTouchStart={handleDragStart}
    >
      <div 
        data-drag-handle
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '12px',
          padding: '10px',
          cursor: 'grab',
          background: '#334155',
          borderRadius: '10px'
        }}
      >
        <span style={{ color: '#94a3b8', fontSize: '16px' }}>☰ Drag to move</span>
        <button 
          onClick={onClose}
          style={{
            position: 'absolute',
            right: '20px',
            padding: '8px 16px',
            borderRadius: '8px',
            background: '#475569',
            color: 'white',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          ✕ Hide
        </button>
      </div>

      <div style={{ maxWidth: mode === 'phone' ? '280px' : 'none', margin: '0 auto' }}>
        {keys.map((row, rowIndex) => (
          <div key={rowIndex} style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
            {row.map((key) => (
              <button
                key={key}
                onClick={() => handleKey(key)}
                style={{
                  padding: key === 'DEL' ? '14px 24px' : key.length > 1 ? '14px 16px' : '14px 20px',
                  borderRadius: '8px',
                  background: key === 'DEL' ? '#dc2626' : key.length > 1 && key !== 'DEL' ? '#2563eb' : '#475569',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '18px',
                  border: 'none',
                  cursor: 'pointer',
                  minWidth: '48px'
                }}
                data-testid={`keyboard-key-${key}`}
              >
                {key === 'DEL' ? '⌫' : key}
              </button>
            ))}
          </div>
        ))}
        {mode !== 'phone' && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
            <button 
              onClick={() => onKeyPress(' ')}
              style={{
                padding: '14px 120px',
                borderRadius: '8px',
                background: '#64748b',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '16px',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              SPACE
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const categoryDetails = {
  disabled_blue_badge: { name: "Disabled Blue Badge", speechName: "Disabled Blue Badge", requiresOCR: true },
  adult_with_child: { name: "Adult with Child", speechName: "Adult with Child", requiresOCR: false },
  extra_wide: { name: "Extra Wide Bay", speechName: "Extra Wide Bay", requiresOCR: false },
  nhs_staff: { name: "NHS Staff", speechName: "NHS Staff", requiresOCR: false },
  military: { name: "Military / Ex-Military", speechName: "Military or Ex-Military", requiresOCR: false }
};

export default function KioskForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  const stateCategory = location.state?.category;
  const urlCategoryId = searchParams.get("category");
  
  const [category, setCategory] = useState(null);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  const [formData, setFormData] = useState({
    vehicle_registration: "",
    full_name: "",
    email: "",
    phone: "",
    nhs_staff_id: "",
    military_id: "",
    children: [{ date_of_birth: "" }]
  });

  const [errors, setErrors] = useState({});
  const [activeField, setActiveField] = useState(null);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [keyboardMode, setKeyboardMode] = useState('text');
  const [hasPlayedSpeech, setHasPlayedSpeech] = useState(false);
  const [speechError, setSpeechError] = useState(false);
  const idleTimerRef = useRef(null);

  const getCategoryDisplayName = () => {
    if (!category) return 'your selected category';
    const details = categoryDetails[category.id];
    return details?.speechName || category.name || 'your selected category';
  };
  
  const guidanceMessage = category 
    ? `You selected ${getCategoryDisplayName()} registration. Please enter your vehicle registration and personal details.`
    : null;
  
  const { needsInteraction, speak: triggerSpeak } = useKioskSpeech('KioskForm', guidanceMessage, !!category);

  const handleSpeakGuidance = () => {
    if (needsInteraction) {
      triggerSpeak();
    } else if (guidanceMessage) {
      const success = speakText(guidanceMessage, () => setSpeechError(true));
      if (success) {
        setHasPlayedSpeech(true);
        setSpeechError(false);
      }
    }
  };

  useEffect(() => {
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => navigate('/kiosk/attract'), IDLE_TIMEOUT * 1000);
    };
    const events = ['mousedown', 'touchstart', 'keydown', 'input'];
    events.forEach(e => document.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [navigate]);

  useEffect(() => {
    if (stateCategory) {
      setCategory(stateCategory);
    } else if (urlCategoryId && categoryDetails[urlCategoryId]) {
      setCategory({
        id: urlCategoryId,
        name: categoryDetails[urlCategoryId].name,
        requiresOCR: categoryDetails[urlCategoryId].requiresOCR
      });
    } else {
      setShouldRedirect(true);
    }
  }, [stateCategory, urlCategoryId]);

  useEffect(() => {
    if (shouldRedirect) {
      navigate("/kiosk/category");
    }
  }, [shouldRedirect, navigate]);

  if (!category) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0d2137' }}>
        <p style={{ color: '#94a3b8', fontSize: '24px' }}>Loading...</p>
      </div>
    );
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleFieldFocus = (field, mode = 'text') => {
    setActiveField(field);
    setKeyboardMode(mode);
    setShowKeyboard(true);
  };

  const handleKeyPress = (key) => {
    if (!activeField) return;
    if (activeField === 'vehicle_registration') {
      handleChange(activeField, (formData[activeField] + key).toUpperCase());
    } else {
      handleChange(activeField, formData[activeField] + key);
    }
  };

  const handleKeyDelete = () => {
    if (!activeField) return;
    handleChange(activeField, formData[activeField].slice(0, -1));
  };

  const handleChildDOBChange = (index, value) => {
    const newChildren = [...formData.children];
    newChildren[index] = { date_of_birth: value };
    setFormData(prev => ({ ...prev, children: newChildren }));
  };

  const addChild = () => {
    setFormData(prev => ({
      ...prev,
      children: [...prev.children, { date_of_birth: "" }]
    }));
  };

  const removeChild = (index) => {
    if (formData.children.length > 1) {
      const newChildren = formData.children.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, children: newChildren }));
    }
  };

  const validateChildAge = (dobString) => {
    if (!dobString) return { valid: false, error: "Date of birth is required" };
    const dob = new Date(dobString);
    const now = new Date();
    let years = now.getFullYear() - dob.getFullYear();
    if (now.getMonth() < dob.getMonth() || (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate())) {
      years -= 1;
    }
    if (years >= 12) {
      return { valid: false, error: `Child is ${years} years old. Registration is only for children under 12.`, years };
    }
    return { valid: true, years };
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.vehicle_registration.trim()) {
      newErrors.vehicle_registration = "Vehicle registration is required";
    }
    if (category.id !== "disabled_blue_badge" && !formData.full_name.trim()) {
      newErrors.full_name = "Full name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (category.id === "nhs_staff" && !formData.nhs_staff_id.trim()) {
      newErrors.nhs_staff_id = "NHS Staff ID is required";
    }
    if (category.id === "military" && !formData.military_id.trim()) {
      newErrors.military_id = "Military ID is required";
    }
    if (category.id === "adult_with_child") {
      const validChildren = formData.children.filter(c => c.date_of_birth);
      if (validChildren.length === 0) {
        newErrors.children = "At least one child's date of birth is required";
      } else {
        for (let i = 0; i < validChildren.length; i++) {
          const ageCheck = validateChildAge(validChildren[i].date_of_birth);
          if (!ageCheck.valid) {
            newErrors.children = ageCheck.error;
            break;
          }
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (validate()) {
      const registrationData = {
        ...formData,
        category: category.id,
        site_id: "site_001",
        kiosk_id: "kiosk_001"
      };
      if (category.id !== "nhs_staff") delete registrationData.nhs_staff_id;
      if (category.id !== "military") delete registrationData.military_id;
      if (category.id !== "adult_with_child") delete registrationData.children;

      if (category.requiresOCR || category.id === "disabled_blue_badge") {
        navigate("/kiosk/badge-intro", { state: { registrationData } });
      } else {
        navigate("/kiosk/processing", { state: { registrationData } });
      }
    }
  };

  return (
    <KioskViewport>
      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: '#0d2137' }} data-testid="kiosk-form">
        {/* STANDARDIZED HEADER */}
        <KioskHeader showDateTime={true} showProgress={true} currentStep={2} />

        {/* MAIN CONTENT - FULL WIDTH */}
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '24px 32px', background: '#e8f4fc', overflow: 'auto', position: 'relative' }}>
          {/* Cityscape Background */}
          <img 
            src="/cityscape.png" 
            alt=""
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '35%',
              objectFit: 'cover',
              objectPosition: 'center bottom',
              opacity: 0.2,
              pointerEvents: 'none'
            }}
          />

          {/* BACK BUTTON - TOP OF CONTENT AREA */}
          <button
            onClick={() => navigate("/kiosk/category")}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 24px',
              borderRadius: '14px',
              backgroundColor: '#1e3a5f',
              border: 'none',
              cursor: 'pointer',
              alignSelf: 'flex-start',
              marginBottom: '20px',
              position: 'relative',
              zIndex: 1
            }}
            data-testid="back-button"
          >
            <ArrowLeft style={{ width: '28px', height: '28px', color: 'white' }} />
            <span style={{ color: 'white', fontWeight: '700', fontSize: '20px', fontFamily: 'Outfit, sans-serif' }}>Back</span>
          </button>

          {/* Avatar and Message - FULL WIDTH */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '20px', position: 'relative', zIndex: 1 }}>
            <div style={{ width: '100px', height: '130px', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', border: '3px solid white', background: '#1e3a5f', flexShrink: 0 }}>
              <img src="/tracy.png" alt="ParkWise Assistant" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
            </div>
            <div style={{ flex: 1, background: 'white', borderRadius: '20px', padding: '16px 20px', boxShadow: '0 4px 16px rgba(0,0,0,0.08)', border: '2px solid #e2e8f0' }}>
              <p style={{ color: '#1e293b', fontSize: '18px', lineHeight: '1.5', margin: 0, fontFamily: 'Outfit, sans-serif' }}>
                Please enter your vehicle registration and personal details for <strong>{category.name}</strong> registration.
              </p>
              <button
                onClick={handleSpeakGuidance}
                style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', background: '#3b82f6', color: 'white', borderRadius: '24px', border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: '600' }}
                data-testid="tap-to-hear"
              >
                <Volume2 style={{ width: '20px', height: '20px' }} />
                {hasPlayedSpeech ? 'Tap to replay guidance' : 'Tap to hear guidance'}
              </button>
              {speechError && <p style={{ marginTop: '8px', color: '#dc2626', fontSize: '14px' }}>Audio not available on this device.</p>}
            </div>
          </div>

          {/* Title */}
          <div style={{ textAlign: 'center', marginBottom: '20px', position: 'relative', zIndex: 1 }}>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '6px', fontFamily: 'Outfit, sans-serif' }}>Enter Your Details</h1>
            <p style={{ fontSize: '18px', color: '#64748b' }}>Registering for: <span style={{ fontWeight: '600', color: '#3b82f6' }}>{category.name}</span></p>
          </div>

          {/* Form - FULL WIDTH */}
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Vehicle Registration */}
              <div>
                <Label htmlFor="vehicle_registration" style={{ fontSize: '17px', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>
                  Vehicle Registration Number *
                </Label>
                <Input
                  id="vehicle_registration"
                  value={formData.vehicle_registration}
                  onChange={(e) => handleChange("vehicle_registration", e.target.value.toUpperCase())}
                  onFocus={() => handleFieldFocus('vehicle_registration', 'text')}
                  placeholder="e.g. AB12 CDE"
                  style={{ height: '60px', fontSize: '24px', textAlign: 'center', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '3px', border: errors.vehicle_registration ? '2px solid #dc2626' : activeField === 'vehicle_registration' ? '2px solid #3b82f6' : '2px solid #e2e8f0', borderRadius: '12px' }}
                  data-testid="input-vehicle-registration"
                  readOnly
                />
                {errors.vehicle_registration && <p style={{ color: '#dc2626', fontSize: '14px', marginTop: '4px' }}>{errors.vehicle_registration}</p>}
              </div>

              {/* Full Name - Hidden for Blue Badge */}
              {category.id !== "disabled_blue_badge" && (
                <div>
                  <Label htmlFor="full_name" style={{ fontSize: '17px', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>Full Name *</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => handleChange("full_name", e.target.value)}
                    onFocus={() => handleFieldFocus('full_name', 'text')}
                    placeholder="Enter your full name"
                    style={{ height: '56px', fontSize: '20px', border: errors.full_name ? '2px solid #dc2626' : activeField === 'full_name' ? '2px solid #3b82f6' : '2px solid #e2e8f0', borderRadius: '12px' }}
                    data-testid="input-full-name"
                    readOnly
                  />
                  {errors.full_name && <p style={{ color: '#dc2626', fontSize: '14px', marginTop: '4px' }}>{errors.full_name}</p>}
                </div>
              )}

              {/* Blue Badge Info Notice */}
              {category.id === "disabled_blue_badge" && (
                <div style={{ background: '#eff6ff', border: '2px solid #bfdbfe', borderRadius: '12px', padding: '14px 18px' }}>
                  <p style={{ color: '#1e40af', fontSize: '15px', margin: 0 }}>
                    <strong>Note:</strong> Your name and badge details will be automatically extracted when you scan your Blue Badge.
                  </p>
                </div>
              )}

              {/* Email */}
              <div>
                <Label htmlFor="email" style={{ fontSize: '17px', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  onFocus={() => handleFieldFocus('email', 'email')}
                  placeholder="your@email.com"
                  style={{ height: '56px', fontSize: '20px', border: errors.email ? '2px solid #dc2626' : activeField === 'email' ? '2px solid #3b82f6' : '2px solid #e2e8f0', borderRadius: '12px' }}
                  data-testid="input-email"
                  readOnly
                />
                {errors.email && <p style={{ color: '#dc2626', fontSize: '14px', marginTop: '4px' }}>{errors.email}</p>}
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone" style={{ fontSize: '17px', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>Phone Number (optional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  onFocus={() => handleFieldFocus('phone', 'phone')}
                  placeholder="e.g. 07700 900123"
                  style={{ height: '56px', fontSize: '20px', border: activeField === 'phone' ? '2px solid #3b82f6' : '2px solid #e2e8f0', borderRadius: '12px' }}
                  data-testid="input-phone"
                  readOnly
                />
              </div>

              {/* Category-specific fields */}
              {category.id === "nhs_staff" && (
                <div>
                  <Label htmlFor="nhs_staff_id" style={{ fontSize: '17px', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>NHS Staff ID *</Label>
                  <Input
                    id="nhs_staff_id"
                    value={formData.nhs_staff_id}
                    onChange={(e) => handleChange("nhs_staff_id", e.target.value)}
                    onFocus={() => handleFieldFocus('nhs_staff_id', 'text')}
                    placeholder="Enter your NHS Staff ID"
                    style={{ height: '56px', fontSize: '20px', border: errors.nhs_staff_id ? '2px solid #dc2626' : activeField === 'nhs_staff_id' ? '2px solid #3b82f6' : '2px solid #e2e8f0', borderRadius: '12px' }}
                    data-testid="input-nhs-staff-id"
                    readOnly
                  />
                  {errors.nhs_staff_id && <p style={{ color: '#dc2626', fontSize: '14px', marginTop: '4px' }}>{errors.nhs_staff_id}</p>}
                </div>
              )}

              {category.id === "military" && (
                <div>
                  <Label htmlFor="military_id" style={{ fontSize: '17px', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>Military / Veterans ID *</Label>
                  <Input
                    id="military_id"
                    value={formData.military_id}
                    onChange={(e) => handleChange("military_id", e.target.value)}
                    onFocus={() => handleFieldFocus('military_id', 'text')}
                    placeholder="Enter your Military ID"
                    style={{ height: '56px', fontSize: '20px', border: errors.military_id ? '2px solid #dc2626' : activeField === 'military_id' ? '2px solid #3b82f6' : '2px solid #e2e8f0', borderRadius: '12px' }}
                    data-testid="input-military-id"
                    readOnly
                  />
                  {errors.military_id && <p style={{ color: '#dc2626', fontSize: '14px', marginTop: '4px' }}>{errors.military_id}</p>}
                </div>
              )}

              {category.id === "adult_with_child" && (
                <div>
                  <Label style={{ fontSize: '17px', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>Children&apos;s Date of Birth *</Label>
                  {formData.children.map((child, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                      <Input
                        type="date"
                        value={child.date_of_birth}
                        onChange={(e) => handleChildDOBChange(index, e.target.value)}
                        style={{ height: '56px', fontSize: '20px', flex: 1, border: '2px solid #e2e8f0', borderRadius: '12px' }}
                        data-testid={`input-child-dob-${index}`}
                      />
                      {formData.children.length > 1 && (
                        <button type="button" onClick={() => removeChild(index)} style={{ padding: '12px', color: '#dc2626', background: '#fef2f2', borderRadius: '10px', border: 'none', cursor: 'pointer' }}>
                          <Trash2 style={{ width: '24px', height: '24px' }} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addChild}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '16px' }}
                    data-testid="add-child-button"
                  >
                    <Plus style={{ width: '20px', height: '20px' }} /> Add another child
                  </button>
                  {errors.children && <p style={{ color: '#dc2626', fontSize: '14px', marginTop: '4px' }}>{errors.children}</p>}
                </div>
              )}
            </div>
          </div>

          {/* CONTINUE + HELP - DIRECTLY AFTER FORM */}
          <div style={{ marginTop: '24px', padding: '20px 24px', background: '#0d2137', borderRadius: '16px', position: 'relative', zIndex: 1 }}>
            {/* Continue button - centered */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <button
                onClick={handleContinue}
                style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '18px 64px', borderRadius: '14px', background: '#16a34a', border: 'none', cursor: 'pointer', boxShadow: '0 6px 20px rgba(22,163,74,0.4)' }}
                data-testid="continue-button"
              >
                <span style={{ fontSize: '24px', fontWeight: '700', color: 'white', fontFamily: 'Outfit, sans-serif' }}>Continue</span>
                <ArrowRight style={{ width: '30px', height: '30px', color: 'white' }} />
              </button>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '14px', background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
              <Phone style={{ width: '24px', height: '24px', color: '#fbbf24' }} />
              <span style={{ color: 'white', fontWeight: '600', fontSize: '18px', fontFamily: 'Outfit, sans-serif' }}>Need help? Call </span>
              <span style={{ color: '#fbbf24', fontWeight: '700', fontSize: '22px', fontFamily: 'Outfit, sans-serif' }}>01292 833107</span>
            </div>
          </div>
        </main>

        {/* Virtual Keyboard */}
        {showKeyboard && (
          <VirtualKeyboard
            onKeyPress={handleKeyPress}
            onDelete={handleKeyDelete}
            onClose={() => { setShowKeyboard(false); setActiveField(null); }}
            mode={keyboardMode}
          />
        )}
      </div>
    </KioskViewport>
  );
}
